(() => {
  const initialized = new WeakSet();

  const makeFullWidth = (root) => {
    const parent = root.closest('.grid__item, .product__info-container, .product__info-wrapper');
    if (!parent) return;
    parent.style.gridColumn = '1 / -1';
    parent.style.justifySelf = 'center';
    parent.style.width = '100%';
  };

  class KiekkoKingiProductCountdown extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedToTimer) return;
      this.isConnectedToTimer = true;
      this.target = Number.parseInt(this.dataset.target || '', 10) * 1000;
      this.parts = {
        days: this.querySelector('[data-part="d"]'),
        hours: this.querySelector('[data-part="h"]'),
        minutes: this.querySelector('[data-part="m"]'),
        seconds: this.querySelector('[data-part="s"]')
      };
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (!Number.isFinite(this.target) || Object.values(this.parts).some((part) => !part)) {
        this.stop(true);
        return;
      }

      this.update();
    }

    disconnectedCallback() {
      this.stop(false);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.isConnectedToTimer = false;
    }

    onVisibilityChange() {
      window.clearTimeout(this.timer);
      if (!document.hidden) this.update();
    }

    stop(hide) {
      window.clearTimeout(this.timer);
      this.timer = 0;
      if (hide) this.hidden = true;
    }

    update() {
      window.clearTimeout(this.timer);
      if (document.hidden || !this.isConnected) return;

      const difference = this.target - Date.now();
      if (difference <= 0) {
        this.stop(true);
        return;
      }

      const totalSeconds = Math.floor(difference / 1000);
      this.parts.days.textContent = String(Math.floor(totalSeconds / 86400));
      this.parts.hours.textContent = String(Math.floor((totalSeconds % 86400) / 3600)).padStart(2, '0');
      this.parts.minutes.textContent = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
      this.parts.seconds.textContent = String(totalSeconds % 60).padStart(2, '0');

      this.timer = window.setTimeout(() => this.update(), Math.min(1000, difference));
    }
  }

  if (!customElements.get('kiekkokingi-product-countdown')) {
    customElements.define('kiekkokingi-product-countdown', KiekkoKingiProductCountdown);
  }

  const initializeProductSwitch = (root) => {
    const select = root.querySelector('[data-kk-product-switch-select]');
    const status = root.querySelector('[data-kk-product-switch-status]');
    const panels = Array.from(root.querySelectorAll('[data-kk-product-switch-panel]'));
    let requestController = null;

    if (!select || !panels.length) return;

    const setStatus = (message) => {
      if (status) status.textContent = message || '';
    };

    const initializeLoadedProducts = (panel) => {
      panel.dispatchEvent(new CustomEvent('theme:rimg:watch', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('kk:products:loaded', {
        detail: { container: panel }
      }));
    };

    const loadPanel = async (panel) => {
      if (!panel || panel.dataset.loaded === 'true' || !panel.dataset.url) return;

      if (requestController) requestController.abort();
      requestController = new AbortController();
      panel.classList.add('is-loading');
      panel.setAttribute('aria-busy', 'true');
      setStatus(root.dataset.loadingText);

      try {
        const response = await fetch(panel.dataset.url, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: requestController.signal
        });

        if (!response.ok) throw new Error(`Product list returned ${response.status}`);

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector('[data-kk-product-switch-response]');
        if (!source) throw new Error('Product list response was empty');

        const productLimit = Number.parseInt(root.dataset.productLimit || '', 10);
        if (Number.isFinite(productLimit)) {
          Array.from(source.querySelectorAll(':scope > .productgrid--item'))
            .slice(productLimit)
            .forEach((item) => item.remove());
        }

        panel.replaceChildren(source);
        panel.dataset.loaded = 'true';
        initializeLoadedProducts(panel);
        setStatus('');
      } catch (error) {
        if (error.name === 'AbortError') return;
        setStatus(root.dataset.errorText);
      } finally {
        panel.classList.remove('is-loading');
        panel.removeAttribute('aria-busy');
        requestController = null;
      }
    };

    const showPanel = (panelId) => {
      let activePanel = null;
      panels.forEach((panel) => {
        const isActive = panel.dataset.kkProductSwitchPanel === panelId;
        panel.hidden = !isActive;
        if (isActive) activePanel = panel;
      });
      if (activePanel) loadPanel(activePanel);
    };

    select.addEventListener('change', () => showPanel(select.value));
    root.addEventListener('shopify:block:select', (event) => {
      const option = Array.from(select.options)
        .find((candidate) => candidate.dataset.blockId === event.detail.blockId);
      if (!option) return;
      select.value = option.value;
      showPanel(option.value);
    });

    root.kkProductSwitchCleanup = () => {
      if (requestController) requestController.abort();
    };
  };

  const initializeUnboxing = (root) => {
    root.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-kk-video-play]');
      if (!button || !root.contains(button)) return;

      const media = button.closest('[data-kk-video-media]');
      const video = media && media.querySelector('video');
      if (!video) return;

      if (!video.getAttribute('src')) {
        const source = video.dataset.src;
        if (!source) return;

        video.controls = true;
        video.src = source;
        video.removeAttribute('data-src');
        video.load();
      }

      try {
        if (video.paused) {
          await video.play();
        } else {
          video.pause();
        }
      } catch (error) {
        media.classList.remove('is-playing');
      }
    });

    root.querySelectorAll('[data-kk-video-media]').forEach((media) => {
      const video = media.querySelector('video');
      const button = media.querySelector('[data-kk-video-play]');
      if (!video || !button) return;

      const updateState = () => {
        const isPlaying = !video.paused && !video.ended;
        media.classList.toggle('is-playing', isPlaying);
        button.setAttribute('aria-pressed', String(isPlaying));
        button.setAttribute(
          'aria-label',
          isPlaying ? button.dataset.pauseLabel : button.dataset.playLabel
        );
      };

      video.addEventListener('play', updateState);
      video.addEventListener('pause', updateState);
      video.addEventListener('ended', updateState);
    });
  };

  const initializeRoot = (root) => {
    if (!root || initialized.has(root)) return;
    initialized.add(root);
    makeFullWidth(root);

    if (root.matches('[data-kk-product-switch]')) initializeProductSwitch(root);
    if (root.matches('[data-kk-unboxing]')) initializeUnboxing(root);
  };

  const initializeScope = (scope) => {
    if (scope.matches && scope.matches('[data-kk-product-extra]')) initializeRoot(scope);
    scope.querySelectorAll?.('[data-kk-product-extra]').forEach(initializeRoot);
  };

  initializeScope(document);

  document.addEventListener('shopify:section:load', (event) => initializeScope(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-kk-product-switch]').forEach((root) => {
      if (root.kkProductSwitchCleanup) root.kkProductSwitchCleanup();
    });
  });
})();
